import { t } from 'i18next';
import { CSSProperties, FC } from 'react';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { LARGE_SIZE } from '../../components/presentational/streamMastheads/shared';
import { Media } from '../../../../../../shared/frontend/components/presentational/media';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { safeCharacterName } from '../../../../../../shared/common/utils';
import { getVideoEmbedLink, IVideoParams, TVideoType } from '../../../../../../shared/common/videoUtils';
import { SpecIcon } from '../../../../../../shared/frontend/scripts/wow/components/SpecIcon';
import { SimpleTooltip_NEXT } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { ZoneContainer } from '../../../../../../shared/frontend/components/ads/zone';
import moment from 'moment';
import { TCharacterSummary } from '../../../../shared/types/raidTypes';
import { FeatureFlagMap } from '../../../../../../shared/common/enums';

export interface VideoEntity {
  id: number;
  videoType: TVideoType;
  videoId: string;
  startVideoTimeSeconds: number;
  duration?: number;
  autoPlay?: boolean;
  character?: TCharacterSummary;
}

type TEntityVideoProps = {
  details: {
    selectedVideo: VideoEntity;
  };
  isMember?: boolean;
  canDeleteSelectedVideo: boolean;
  currentFeatures?: FeatureFlagMap;
  style?: CSSProperties;
  videos: VideoEntity[];
  handleVideoSelect: (video: any) => void;
  handleRemoveVideo: (videoId: number) => void;
};

const DESKTOP_VIDEO_SIZE = {
  width: Math.round((250 * 16) / 9),
  height: 250
};

type TVideoFrameProps = {
  fluid?: boolean;
  selectedVideoId: number;
  selectedVideoUrl: string;
  size: { width: number; height: number };
};

function VideoFrame({ fluid = false, selectedVideoId, selectedVideoUrl, size }: TVideoFrameProps) {
  return (
    <div
      style={
        fluid
          ? { width: '100%', maxWidth: size.width, margin: '0 auto' }
          : {
              width: size.width,
              maxWidth: '100%',
              flexShrink: 0
            }
      }
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${size.width} / ${size.height}`,
          backgroundColor: '#000'
        }}
      >
        <iframe
          key={selectedVideoId}
          src={selectedVideoUrl}
          frameBorder="0"
          allowFullScreen
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%'
          }}
        />
      </div>
    </div>
  );
}

const EventVideosPanel = withSiteMeta((props: TEntityVideoProps) => {
  const { details, canDeleteSelectedVideo, currentFeatures, videos } = props;
  const { selectedVideo } = details;
  if (!selectedVideo) {
    return <></>;
  }

  const videoParams: IVideoParams = selectedVideo.autoPlay ? { autoPlay: true, mute: true } : { autoPlay: false };
  if (selectedVideo.startVideoTimeSeconds) {
    videoParams.start = selectedVideo.startVideoTimeSeconds;
  }

  const selectedVideoUrl = getVideoEmbedLink(selectedVideo.videoId, selectedVideo.videoType, videoParams);

  const handleVideoSelect = (video: any) => {
    props.handleVideoSelect(video);
  };

  const onConfirm = (e: any) => {
    e.stopPropagation();
    const confirmation = window.confirm(t('videos:delete-prompt'));
    if (confirmation) {
      props.handleRemoveVideo(selectedVideo.id);
    }
  };

  let multiPovCount = 0;
  const videoTitle = (video: any) => {
    if (video.character && video.character.spec && video.character.class) {
      return safeCharacterName(video.character.name);
    }
    return t('videos:panel.multi-pov-label', { count: ++multiPovCount });
  };

  const povList: any[] = [];
  for (const video of videos) {
    const title = videoTitle(video);
    const classes: string[] = ['rio-realm-link', 'rio-pointer'];
    if (selectedVideo.id === video.id) {
      classes.push('text-bold text-underline');
    }

    if (video.character) {
      classes.push(`class-color--${video.character.class.slug}`);
      povList.push(
        <>
          <SimpleTooltip_NEXT
            tooltip={
              <span className={`class-color--${video.character.class.slug} text-bold`}>
                {t('common:class-spec', {
                  class: t(`game:classes.${video.character.class.slug}.name`),
                  spec: t(`game:specs.${video.character.class.slug}.${video.character.spec.slug}.name`)
                })}{' '}
                {!!video.duration &&
                  `(${t('videos:video-duration')} - ${moment.utc(video.duration * 1000).format('HH:mm:ss')})`}
              </span>
            }
            className="slds-m-left--small"
          >
            <span className={classes.join(' ')} onClick={() => handleVideoSelect(video)}>
              <span className="slds-avatar slds-avatar--small rio-border--light slds-m-right--xx-small">
                <SpecIcon class={video.character.class.slug} spec={video.character.spec.slug} />
              </span>
              {title}
            </span>
            {canDeleteSelectedVideo && video.id === selectedVideo.id && (
              <i
                className="fa-solid fa-trash text-white rio-hover-color-red rio-pointer slds-m-left--x-small"
                style={{ opacity: 0.25 }}
                onClick={onConfirm}
              ></i>
            )}
          </SimpleTooltip_NEXT>
        </>
      );
    } else {
      classes.push('text-raiderio slds-m-left--small');
      povList.push(
        <>
          <SimpleTooltip_NEXT
            tooltip={
              <span className="text-bold">
                {`${t('videos:video-duration')} - ${moment.utc(video.duration! * 1000).format('HH:mm:ss')}`}
              </span>
            }
            className="d-inline-block"
            hideTooltip={!video.duration}
          >
            <span className={classes.join(' ')} onClick={() => handleVideoSelect(video)}>
              {title}
            </span>
          </SimpleTooltip_NEXT>
          {canDeleteSelectedVideo && video.id === selectedVideo.id && (
            <i
              className="fa-solid fa-trash text-white rio-hover-color-red rio-pointer slds-m-left--x-small"
              style={{ opacity: 0.25 }}
              onClick={onConfirm}
            ></i>
          )}
        </>
      );
    }
  }

  return (
    <div
      id="rio-video-container"
      className="slds-p-around--medium"
      style={{
        ...props.style,
        backgroundImage: `url(${asset('/images/bgtiles/diamonds_bw.jpg')})`,
        backgroundSize: '25%'
      }}
    >
      <div
        className="slds-text-align--center slds-text-heading--small slds-m-bottom--small"
        style={{
          whiteSpace: 'pre-wrap'
        }}
      >
        {t('videos:panel.choose-pov-label')}:{povList}
      </div>
      <Media at="sm">
        <div className="slds-grid slds-grid--align-center">
          <div className="slds-col slds-size--1-of-1">
            <VideoFrame
              fluid={true}
              selectedVideoId={selectedVideo.id}
              selectedVideoUrl={selectedVideoUrl}
              size={LARGE_SIZE}
            />
          </div>
        </div>
      </Media>
      <Media greaterThan="sm">
        <Media lessThan="lg">
          <div className="slds-grid slds-grid--align-center">
            <div className="slds-col" style={{ flex: '0 0 auto' }}>
              <VideoFrame
                selectedVideoId={selectedVideo.id}
                selectedVideoUrl={selectedVideoUrl}
                size={DESKTOP_VIDEO_SIZE}
              />
            </div>
          </div>
        </Media>
        <Media greaterThanOrEqual="lg">
          <div
            className="slds-grid slds-grid--align-center slds-grid--vertical-align-start slds-wrap"
            style={{ gap: '1rem' }}
          >
            <div className="slds-col" style={{ flex: '0 0 auto' }}>
              <VideoFrame
                selectedVideoId={selectedVideo.id}
                selectedVideoUrl={selectedVideoUrl}
                size={DESKTOP_VIDEO_SIZE}
              />
            </div>
            {!currentFeatures?.no_ads && (
              <div className="slds-col" style={{ flex: '0 0 auto' }}>
                <div
                  style={{
                    width: 300,
                    maxWidth: 300,
                    minHeight: 250
                  }}
                >
                  <ZoneContainer tag="section" className="slds-text-align--center" type="mrec_video_companion" />
                </div>
              </div>
            )}
          </div>
        </Media>
      </Media>
    </div>
  );
});

export default EventVideosPanel as unknown as FC<TEntityVideoProps>;
